<script lang="ts" setup>
const { t } = useI18n();
const localePath = useLocalePath();
const { user, clear } = useUserSession();
const layout = await useLayout();

const impersonationIsActive = layout?.value?.impersonationIsActive
  ?.replaceAll("{userName}", user.value?.name || "")
  ?.replaceAll("{userEmail}", user.value?.email || "");

const bannerRef = useTemplateRef<HTMLElement>("bannerRef");
const bannerHeight = ref(0);

const slideoverCount = useIsSlideoverOpen();

// ResizeObserver fires on mount and on every resize, keeping the spacer in sync
// when the banner wraps to a second line (e.g. on narrow viewports).
let resizeObserver: ResizeObserver | undefined;

watch(bannerRef, (el, _, onCleanup) => {
  resizeObserver?.disconnect();

  if (el) {
    resizeObserver = new ResizeObserver(() => {
      bannerHeight.value = el.offsetHeight;
    });

    resizeObserver.observe(el);
  }

  onCleanup(() => resizeObserver?.disconnect());
});

async function revertImpersonation() {
  try {
    const data = await $fetch("/api/ecom/auth/impersonate/revert", {
      method: "POST",
    });

    if (data?.success) {
      // technically we don't need to do full page navigation, but it's easier for state (external = true)
      await navigateTo(localePath("/account/dashboard"), { external: true });
    }
    else {
      await clear();
    }
  }
  catch (error) {
    console.error("Error reverting impersonation:", error);
    useFailToast(t("common.title.error"), createFetchErrorMessage(error));
  }
}
</script>

<template>
  <!-- Spacer preserves document flow while the actual banner is teleported -->
  <div v-if="user?.isImpersonation" :style="{ height: `${bannerHeight}px` }" aria-hidden="true" />

  <Teleport to="body">
    <div
      v-if="user?.isImpersonation"
      ref="bannerRef"
      class="bg-primary fixed top-0 z-999999 w-full text-sm text-white"
      :class="{ invisible: slideoverCount > 0 }"
    >
      <div class="p-xs gap-md container mx-auto flex flex-col items-center font-medium md:flex-row md:justify-between">
        <div class="gap-xs flex items-center justify-center md:justify-start">
          <span class="text-center md:text-start">
            {{ impersonationIsActive }}
          </span>
        </div>

        <div>
          <UButton variant="link" :ui="{ base: 'text-white hover:text-primary-100' }" leading-icon="i-ph-sign-out" @click="revertImpersonation">
            {{ layout?.revertImpersonation }}
          </UButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>

</style>
